import { gql } from '@apollo/client'

export interface TransactionData {
  transaction: Transaction
}

export interface ProcessPaymentTransactionData {
  processPaymentTransaction: Transaction
}

export interface ProcessPaymentTransactionInput {
  input: {
    registerID: string
    retailerPaymentTypeID: string
    registerSalePaymentID: string
    amount: number
    currency: string
  }
}

export interface ProcessRefundTransactionData {
  processRefundTransaction: Transaction
}

export interface ProcessRefundTransactionInput {
  input: {
    registerID: string
    retailerPaymentTypeID: string
    registerSalePaymentID: string
    amount: number
    currency: string
    originalRegisterSalePaymentID?: string
  }
}

export interface ReverseTransactionData {
  reverseTransaction: Transaction
}

export interface ReverseTransactionInput {
  input: {
    registerID: string
    retailerPaymentTypeID: string
    registerSalePaymentID: string
  }
}

export interface PaymentTransactionInput {
  registerID: string
  retailerPaymentTypeID: string
  registerSalePaymentID: string
  amount: number
  currency: string
}

export interface Transaction {
  id: string
  status: TransactionStatus
  registerSalePaymentID: string
  tenders: Tender[]
}

type TransactionStatus =
  | 'CANCELLED'
  | 'CANCELLED_BAD_CARD'
  | 'CANCELLED_BUSY'
  | 'COMPLETED'
  | 'DECLINED'
  | 'REFUNDED'
  | 'REVERSED'
  | 'TIMEOUT'
  | 'UNKNOWN'
  | 'PROCESSING'

export interface Tender {
  id: string
  type: TenderType
  amount: number
  status: TenderStatus
  externalTransactionID: string
  emv: EMV
  cardDetails: CardDetails
  providerFields: { [key: string]: string }
  createdAt: string
}

type TenderStatus =
  | 'APPROVED'
  | 'APPROVED_PIN'
  | 'APPROVED_SIGNATURE'
  | 'CANCELLED'
  | 'DECLINED'
  | 'REFUNDED'
  | 'REVERSED'
  | 'UNKNOWN'

export type TenderType =
  | 'SALE'
  | 'REFUND'
  | 'REVERSAL'
  | 'CASH'
  | 'CREDIT'
  | 'DEBIT'
  | 'TIP'

interface EMV {
  aid: string
  applicationLabel: string
  cryptogramType: string
  cryptogram: string
  pinStatement: string
  tags: Tag[]
  applicationPreferredName: string
}

interface Tag {
  key: string
  value: string
}

interface CardDetails {
  entryMethod: string
  card: Card
  authResult: string
  authCode: string
}

interface Card {
  brand: string
  type: string
  lastFour: string
  fingerPrint: string
}

export const TransactionFields = gql`
  fragment TransactionFields on Transaction {
    id
    status
    registerSalePaymentID
    tenders {
      id
      type
      createdAt
      amount
      status
      externalTransactionID
      emv {
        aid
        applicationLabel
        cryptogramType
        cryptogram
        pinStatement
        tags {
          key
          value
        }
        applicationPreferredName
      }
      cardDetails {
        entryMethod
        card {
          brand
          type
          lastFour
          fingerPrint
        }
        authResult
        authCode
      }
      providerFields
    }
  }
`

export const PROCESS_PAYMENT_TRANSACTION = gql`
  ${TransactionFields}
  mutation ProcessPaymentTransaction($input: PaymentTransactionInput!) {
    processPaymentTransaction(input: $input) {
      ...TransactionFields
    }
  }
`

export const GET_TRANSACTION = gql`
  ${TransactionFields}
  query Transaction($registerSalePaymentID: ID!) {
    transaction(
      registerSalePaymentID: $registerSalePaymentID
      enableResync: true
    ) {
      ...TransactionFields
    }
  }
`

export const PROCESS_REFUND_TRANSACTION = gql`
  ${TransactionFields}
  mutation ProcessRefundTransaction($input: RefundTransactionInput!) {
    processRefundTransaction(input: $input) {
      ...TransactionFields
    }
  }
`

export const REVERSE_TRANSACTION = gql`
  ${TransactionFields}
  mutation ReverseTransaction($input: ReverseTransactionInput!) {
    reverseTransaction(input: $input) {
      ...TransactionFields
    }
  }
`
